import { CodeEditor } from '@acrodata/code-editor';
import { ChangeDetectionStrategy,Component, computed, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { languages } from '@codemirror/language-data';

import { PageStatus } from 'app/layout/content';
import { AdminComponentBase } from 'app/layout/content';
import { PageSimpleContentComponent } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';

import { DesignerCustomizationsService } from './designer-customizations.service';

@Component({
    selector: 'admin-store-designer-custom-css',
    templateUrl: './store-designer-custom-css.component.html',
    styleUrls: ['./store-designer-custom-css.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, MatButton, SaveButtonComponent, ErrorComponent, FormsModule, MatIcon, CodeEditor]
})
export class StoreDesignerCustomCssComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly designerCustomizationsService = inject(DesignerCustomizationsService);
    private readonly confirmDialogService = inject(ConfirmDialogService);

    savedCss: string;
    css: string;
    storeId: string;
    languages = languages;

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.subscribeWithGenericLoadingErrorHandling(this.designerCustomizationsService.getStoreFiles(this.storeId), storeFiles => {
                if (!storeFiles.css?.url) {
                    this.css = this.savedCss = '';
                    this.pageStatus = PageStatus.loaded;
                } else {
                    this.designerCustomizationsService.getCustomizationFile(storeFiles.css?.url).subscribe({
                        next: (css) => {
                            this.css = this.savedCss = css;
                            this.pageStatus = PageStatus.loaded;
                        },
                        error: (err) => {
                            this.pageError = err;
                            this.pageStatus = PageStatus.loadError;
                        }
                    });
                }
            });
        }));
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    public get canSave(): Signal<boolean> {
        return computed(() => this.savedCss !== this.css);
    }

    save(): void {
        this.subscribeWithGenericSavingErrorHandling(this.designerCustomizationsService.updateCssFile({
            storeId: this.storeId,
            content: this.css
        }), () => {
            this.savedCss = this.css;
        });
    }

    public get canDelete(): Signal<boolean> {
        return computed(() => this.pageStatus === PageStatus.loaded && this.savedCss !== '');
    }

    delete(): void {
        this.confirmDialogService
            .confirm(
                'Confirmation',
                `Do you want to delete the custom CSS for this store?`
            ).subscribe(() => {
                this.subscribeWithGenericSavingErrorHandling(this.designerCustomizationsService.deleteCssFile({
                    storeId: this.storeId
                }), () => {
                    this.css = this.savedCss = '';
                });
            }
        );
    }

    async onFilesChanged(files: FileList) {
        if (!files.length) {
            return;
        }
        this.css = await files[0].text();
    }

    handleSaveEditor(event: KeyboardEvent) {
        if (event.key === 's' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault(); // Prevent browser's save dialog
            this.save();
        }
    }
}
